import { Root } from "mdast";
import { Plugin } from "unified";
import { visit } from "unist-util-visit";
import { Paper } from "helpers/api";

/**
 * Options for the remarkCitationQuoteNumbering plugin
 */
interface RemarkCitationQuoteNumberingOptions {
  papers?: Paper[];
}

/**
 * Remark plugin that adds quote indices to paper_cite nodes in the AST.
 *
 * This plugin processes:
 * - paper_cite nodes from `[1]` or `<paper_cite>` tags that have quotes
 *
 * The plugin traverses the AST and:
 * - Tracks how many quotes have been seen for each paper_id
 * - Assigns a sequential quoteIndex (0-based) to each quote within a paper
 * - Adds the quoteIndex as a property to the node for use by components
 *
 * This ensures quote citations are numbered correctly (1.1, 1.2, 1.3, etc.) based on document
 * order, regardless of component mount timing.
 *
 * Example transformation:
 * Input:  { type: "paper_cite", data: { hProperties: { paperIndex: 0, quote: "text" } } }
 * Output: { type: "paper_cite", data: { hProperties: { paperIndex: 0, quote: "text", quoteIndex: 0 } } }
 */
export const remarkCitationQuoteNumbering: Plugin<
  [RemarkCitationQuoteNumberingOptions?],
  Root
> = (options = {}) => {
  return (tree) => {
    const { papers = [] } = options;

    // Track how many citations/quotes we've seen for each paper to assign correct indices
    const paperCitationCounts: Record<string, number> = {};

    // Helper function to get paper_id from different node types
    const getPaperIdFromNode = (node: any): string | null => {
      const props = node.data?.hProperties;
      if (!props) return null;

      // Direct paper_id (from <cite> or <paper_cite>)
      if (props.paper_id || props.paperId) {
        return props.paper_id || props.paperId;
      }

      // Convert paperIndex to paper_id (from [1] citations)
      if (typeof props.paperIndex === "number" && papers[props.paperIndex]) {
        return papers[props.paperIndex].paper_id;
      }

      return null;
    };

    visit(tree, (node: any) => {
      // Process only paper_cite nodes that have quotes
      if (node.type === "paper_cite") {
        const props = node.data?.hProperties;
        const hasQuote = props && props.quote;

        // Only process nodes with quotes
        if (hasQuote) {
          const paperId = getPaperIdFromNode(node);

          if (paperId) {
            // Track quote index within this specific paper (0-based)
            // This ensures citations from the same paper are numbered correctly (1st, 2nd, 3rd, etc.)
            const quoteIndex = paperCitationCounts[paperId] || 0;
            paperCitationCounts[paperId] = quoteIndex + 1;

            // Update the node with the quoteIndex
            if (!node.data) {
              node.data = {};
            }
            if (!node.data.hProperties) {
              node.data.hProperties = {};
            }
            node.data.hProperties.quoteIndex = quoteIndex;
          }
        }
      }
    });
  };
};
